import { API_BASE, API } from "@/config/apiBase";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";


// Log API base for debugging

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle OAuth error redirects
  useEffect(() => {
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    
    if (error) {
      // Map error codes to user-friendly messages
      const errorMessages = {
        'login_expired': message || 'Login session expired. Please try again.',
        'invalid_state': 'Login session expired. Please try again.',
        'google_auth_failed': 'Google login failed. Please try again.',
        'missing_params': 'Login incomplete. Please try again.',
        'server_error': 'Server error during login. Please try again.'
      };
      
      const displayMessage = errorMessages[error] || 'Login failed. Please try again.';
      toast.error(displayMessage);
      
      // Clear error params from URL
      setSearchParams({}, { replace: true });
    }
    
    // Check for invite token in URL and preserve it
    const inviteId = searchParams.get('invite');
    if (inviteId) {
      console.log("[Login] Preserving family invite from URL:", inviteId);
      localStorage.setItem("pendingFamilyInvite", inviteId);
    }
  }, [searchParams, setSearchParams]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        console.log("[Auth] Token stored in localStorage");
      }
      
      // Check for pending family invite - redirect to settings if exists
      const pendingInvite = localStorage.getItem("pendingFamilyInvite");
      if (pendingInvite) {
        console.log("[Auth] Pending family invite found, redirecting to settings");
        toast.success("Welcome! Processing your family invite...");
        navigate("/settings?tab=family", { state: { user: response.data }, replace: true });
      } else {
        toast.success("Welcome back!");
        navigate("/dashboard", { state: { user: response.data }, replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid credentials";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Direct Google OAuth - redirects to our backend which handles Google OAuth
  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE}/api/auth/google/login`;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" className="inline-flex items-center text-[#6D6F7C] hover:text-[#3D405B] mb-8 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Welcome Back
            </h1>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#3D405B]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-[#3D405B]">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#E07A5F] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#3D405B] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="login-button"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#F4F1EA]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#FDFBF7] px-4 text-[#9CA3AF]">or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full h-14 text-[#3D405B] font-medium"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-[#6D6F7C] mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#E07A5F] font-semibold hover:underline" data-testid="register-link">
              Sign up free
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#3D405B] p-12">
        <motion.div 
          className="max-w-lg"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <img 
            src="https://images.pexels.com/photos/3754676/pexels-photo-3754676.jpeg" 
            alt="Person smiling on phone call"
            className="rounded-3xl shadow-2xl"
          />
          <div className="mt-8 text-center">
            <p className="text-white/90 text-xl leading-relaxed" style={{ fontFamily: 'Fraunces, serif' }}>
              "The best time to call someone you love was yesterday. The second best time is now."
            </p>
            <p className="text-white/60 mt-4">— OuttaTouch Philosophy</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
